import { useEffect, useMemo, useRef, useState } from "react";
import ReactGlobe from "react-globe.gl";
import * as THREE from "three";

// Lat/Lng of Dubai (origin / hub)
const DUBAI = { lat: 25.2048, lng: 55.2708, name: "Dubai" };

// Major B2B destinations worldwide
const DESTINATIONS = [
  { lat: 51.5074, lng: -0.1278, name: "London" },
  { lat: 40.7128, lng: -74.006, name: "New York" },
  { lat: 1.3521, lng: 103.8198, name: "Singapore" },
  { lat: 35.6762, lng: 139.6503, name: "Tokyo" },
  { lat: 22.3193, lng: 114.1694, name: "Hong Kong" },
  { lat: 19.076, lng: 72.8777, name: "Mumbai" },
  { lat: 50.1109, lng: 8.6821, name: "Frankfurt" },
  { lat: 6.5244, lng: 3.3792, name: "Lagos" },
  { lat: -1.2921, lng: 36.8219, name: "Nairobi" },
  { lat: -33.8688, lng: 151.2093, name: "Sydney" },
  { lat: -23.5505, lng: -46.6333, name: "São Paulo" },
  { lat: 30.0444, lng: 31.2357, name: "Cairo" },
];

const COUNTRIES_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json";

// Dark sphere material — solid, no external texture
function makeGlobeMaterial() {
  return new THREE.MeshPhongMaterial({
    color: new THREE.Color("#070b1f"),
    emissive: new THREE.Color("#0a1438"),
    emissiveIntensity: 0.65,
    shininess: 16,
    transparent: false,
  });
}

export default function Globe({ size = 600 }) {
  const globeRef = useRef(null);
  const wrapperRef = useRef(null);
  const [dims, setDims] = useState({ w: size, h: size });
  const [ready, setReady] = useState(false);
  const [countries, setCountries] = useState({ features: [] });

  // Load country polygons (cached after first fetch)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [topojsonClient, topoData] = await Promise.all([
          import("topojson-client"),
          fetch(COUNTRIES_URL).then((r) => r.json()),
        ]);
        if (cancelled) return;
        const features = topojsonClient.feature(
          topoData,
          topoData.objects.countries
        );
        setCountries(features);
      } catch {
        // Fail silently — globe still works without country outlines
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Measure container responsively
  useEffect(() => {
    if (!wrapperRef.current) return undefined;
    const update = () => {
      const w = wrapperRef.current?.offsetWidth || size;
      setDims({ w, h: w });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [size]);

  // Configure auto-rotation, lighting and POV after the globe mounts
  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;

    // Center the view on Dubai
    g.pointOfView({ lat: 25, lng: 35, altitude: 2.1 }, 0);

    const controls = g.controls();
    if (controls) {
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.45;
      controls.enableZoom = false;
      controls.enablePan = false;
      controls.minPolarAngle = Math.PI / 4;
      controls.maxPolarAngle = Math.PI - Math.PI / 4;
    }

    // Soften default lighting and add a violet rim light
    try {
      const scene = g.scene();
      scene.children
        .filter((c) => c.isLight && c.type === "AmbientLight")
        .forEach((l) => {
          l.intensity = 0.65;
          l.color = new THREE.Color("#cfd4ff");
        });

      const rim = new THREE.PointLight("#7A5CFF", 1.6, 0, 0);
      rim.position.set(-200, 60, 120);
      scene.add(rim);

      const blue = new THREE.PointLight("#164BFF", 1.2, 0, 0);
      blue.position.set(180, -40, 100);
      scene.add(blue);
    } catch {
      // ignore
    }

    setReady(true);
  }, [dims.w]);

  // Arc data: Dubai → each destination
  const arcs = useMemo(
    () =>
      DESTINATIONS.map((d) => ({
        startLat: DUBAI.lat,
        startLng: DUBAI.lng,
        endLat: d.lat,
        endLng: d.lng,
        color: ["rgba(122,92,255,0.05)", "#8B6DFF", "#164BFF"],
      })),
    []
  );

  // Markers
  const points = useMemo(
    () => [
      { ...DUBAI, isHub: true, size: 0.9, color: "#8B6DFF" },
      ...DESTINATIONS.map((d) => ({
        ...d,
        isHub: false,
        size: 0.45,
        color: "#A78BFF",
      })),
    ],
    []
  );

  // Ripple ring for Dubai
  const rings = useMemo(
    () => [{ lat: DUBAI.lat, lng: DUBAI.lng, color: "#7A5CFF" }],
    []
  );

  return (
    <div
      data-testid="hero-globe"
      ref={wrapperRef}
      className="relative w-full mx-auto"
      style={{ aspectRatio: "1 / 1", maxWidth: size }}
    >
      {/* Halos behind the globe */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, rgba(22,75,255,0.55), transparent 65%)",
          filter: "blur(38px)",
        }}
      />
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(60% 60% at 65% 30%, rgba(122,92,255,0.45), transparent 60%)",
          filter: "blur(44px)",
        }}
      />

      <div
        className="absolute inset-0 flex items-center justify-center transition-opacity duration-700"
        style={{ opacity: ready ? 1 : 0 }}
      >
        <ReactGlobe
          ref={globeRef}
          width={dims.w}
          height={dims.h}
          backgroundColor="rgba(0,0,0,0)"
          showAtmosphere
          atmosphereColor="#5b6bff"
          atmosphereAltitude={0.22}
          globeMaterial={useMemo(makeGlobeMaterial, [])}
          // COUNTRY POLYGONS — give the globe an unmistakable Earth shape
          polygonsData={countries.features || []}
          polygonAltitude={0.012}
          polygonCapColor={() => "rgba(150,170,255,0.45)"}
          polygonSideColor={() => "rgba(22,75,255,0.25)"}
          polygonStrokeColor={() => "rgba(220,228,255,0.85)"}
          // ARCS
          arcsData={arcs}
          arcColor="color"
          arcAltitudeAutoScale={0.5}
          arcStroke={0.45}
          arcDashLength={0.45}
          arcDashGap={1.2}
          arcDashInitialGap={() => Math.random() * 2}
          arcDashAnimateTime={2400}
          // POINTS
          pointsData={points}
          pointLat="lat"
          pointLng="lng"
          pointColor="color"
          pointAltitude={(p) => (p.isHub ? 0.04 : 0.012)}
          pointRadius="size"
          pointResolution={12}
          // RIPPLES around Dubai
          ringsData={rings}
          ringColor={() => "#7A5CFF"}
          ringMaxRadius={3.2}
          ringPropagationSpeed={1.8}
          ringRepeatPeriod={1400}
          ringAltitude={0.012}
        />
      </div>

      {/* Decorative rotating rings (outer) */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="absolute w-[104%] h-[104%] rounded-full border border-white/[0.06] animate-[spin_60s_linear_infinite]" />
        <div className="absolute w-[116%] h-[116%] rounded-full border border-white/[0.04] animate-[spin_90s_linear_infinite_reverse]" />
      </div>

      {/* Dubai HUB callout chip */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute glass-card rounded-xl px-3 py-2 shadow-lg"
          style={{ top: "44%", left: "55%" }}
          data-testid="hero-hub-chip"
        >
          <div className="flex items-center gap-2">
            <span className="relative inline-flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-[#7A5CFF] opacity-70 animate-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-[#8B6DFF] shadow-[0_0_12px_rgba(139,109,255,0.9)]" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#E2E3EA]">
              Hub · Dubai
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
